import * as React from 'react';
import { FolderIcon, Users, GraduationCap, Box } from 'lucide-react';

import { NavMain } from '@/components/nav-main';
import { NavUser } from '@/components/nav-user.jsx';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGetRequest } from '@/shared/hooks/requests';
import { PROJECTS, USER_INFO } from '@/shared/utils/urls.js';
import { useEffect } from 'react';
import { LangSelector } from '@/shared/ui/LangSelector.jsx';
import VersionFooter from '@/components/VersionFooter.jsx';
import { useTranslation } from 'react-i18next';

const getIframeData = (t) => [
  {
    title: t('navigation:documents'),
    icon: FolderIcon,
    id: 2,
    isActive: true,
    items: [
      { title: t('navigation:my-docs'), url: '/my-docs' },
      { title: t('navigation:shared'), url: '/shared-docs' },
      { title: t('navigation:templates'), url: '/templates' },
      { title: t('navigation:skills'), url: '/skills' },
    ],
  },
];

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { t } = useTranslation(['common', 'navigation']);
  const userType = localStorage.getItem('userType');
  const location = useLocation();
  const loadOrgList = useGetRequest({ url: PROJECTS });
  const loadUserInfo = useGetRequest({ url: USER_INFO });
  const orgList = loadOrgList.response ? loadOrgList.response.projects : [];
  const userInfo = loadUserInfo.response;
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';

  const navigate = useNavigate();

  useEffect(() => {
    if (userType === 'admin' && !loadOrgList.loading && !loadOrgList.response) {
      loadOrgList.request();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType]);

  useEffect(() => {
    if (userType === 'admin' && !loadUserInfo.loading && !loadUserInfo.response) {
      loadUserInfo.request();
    }
    // eslint-disable-next-line  react-hooks/exhaustive-deps
  }, [userType]);

  // Navigation items with counts
  const navItems = [
    {
      title: t('navigation:projects'),
      icon: Users,
      count: orgList?.length || 0,
      path: '/projects',
    },
    {
      title: t('navigation:documents'),
      icon: GraduationCap,
      count: 245, // TODO: Get actual count from API
      path: '/my-docs',
    },
  ].map(item => ({
    ...item,
    isActive: location.pathname === item.path,
  }));

  return (
    <Sidebar
      collapsible="icon"
      {...props}
      className="bg-sidebar [&_[data-slot=sidebar-inner]]:bg-sidebar [&_[data-slot=sidebar-container]]:!border-0 [&_[data-slot=sidebar-container]]:!border-r-0 [&_[data-slot=sidebar-container]]:!border-l-0"
    >
      <SidebarHeader>
        <div onClick={() => navigate('/projects')} className="flex items-center gap-2 px-3 py-4 cursor-pointer">
          {collapsed ? (
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
              <Box className="w-5 h-5 text-white" />
            </div>
          ) : (
            <>
              <img src="/logo-docs.svg" alt="logo" className="w-[160px]" />
            </>
          )}
        </div>
      </SidebarHeader>
      <SidebarContent>
        {userType === 'admin' ? (
          <div className="px-3 space-y-1">
            {navItems.map((item) => (
              <div
                key={item.title}
                onClick={() => navigate(item.path)}
                className={`
                  flex items-center justify-between px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-200
                  ${item.isActive
                    ? 'text-white shadow-sm'
                    : 'hover:bg-gray-50 text-gray-700'
                  }
                `}
                style={item.isActive ? {
                  background: 'linear-gradient(135deg, #4185F6 0%, #6FA4F9 100%)',
                } : {}}
              >
                <div className="flex items-center gap-3">
                  <item.icon className={`w-5 h-5 ${item.isActive ? 'text-white' : 'text-gray-600'}`} />
                  {!collapsed && (
                    <span className={`text-sm font-medium ${item.isActive ? 'text-white' : 'text-gray-700'}`}>
                      {item.title}
                    </span>
                  )}
                </div>
                {!collapsed && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${item.isActive ? 'bg-white/20 backdrop-blur-sm text-white' : 'bg-gray-200 text-gray-600'}`}>
                    {item.count}
                  </span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <NavMain
            items={getIframeData(t)}
            setData={loadOrgList.request}
            userType={userType}
          />
        )}
      </SidebarContent>
      <SidebarFooter>
        <div className="px-3 space-y-3">
          <LangSelector />
          <NavUser user={userInfo} />
          {!collapsed && <VersionFooter />}
        </div>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
